import React, { useEffect, useState, useRef } from 'react';
import { adminApi } from '../api/client';
import './css/admin-media.css';

const CATEGORIES = ['artwork', 'characters', 'promo', 'community', 'videos'];

const EMPTY_META = {
  title: '',
  description: '',
  category: 'artwork',
  status: 'draft',
};

const VIDEO_EXT = /\.(mp4|webm|mov|m4v)$/i;

function previewTypeFromFile(file) {
  if (file.type.startsWith('video/')) return 'video';
  if (VIDEO_EXT.test(file.name)) return 'video';
  return 'image';
}

function previewTypeFromUrl(url, fallback = 'image') {
  if (VIDEO_EXT.test(url.split('?')[0])) return 'video';
  return fallback;
}

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_META);
  const [editingId, setEditingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('image');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    adminApi.getMedia().then(setItems).catch((e) => setError(e.message));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => () => {
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const resetForm = () => {
    setForm(EMPTY_META);
    setEditingId(null);
    setPreviewUrl('');
    setPreviewType('image');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileChange = () => {
    const file = fileRef.current?.files?.[0];
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    if (file) {
      setPreviewUrl(URL.createObjectURL(file));
      setPreviewType(previewTypeFromFile(file));
      if (!form.title) setForm((f) => ({ ...f, title: file.name.replace(/\.[^.]+$/, '') }));
    } else {
      setPreviewUrl('');
      setPreviewType('image');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || '',
      description: item.description || '',
      category: item.category || 'artwork',
      status: item.status || 'draft',
    });
    setPreviewUrl(item.file_url || '');
    setPreviewType(item.file_type === 'video' ? 'video' : previewTypeFromUrl(item.file_url || ''));
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      if (editingId) {
        await adminApi.updateMedia(editingId, form);
        resetForm();
        load();
        return;
      }

      const file = fileRef.current?.files?.[0];
      if (!file) {
        setError('Select a file to upload');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', form.title || file.name);
      formData.append('description', form.description);
      formData.append('category', form.category);
      formData.append('status', form.status);

      await adminApi.uploadMedia(formData);
      resetForm();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this upload?')) return;
    try {
      await adminApi.deleteMedia(id);
      if (editingId === id) resetForm();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const isVideoPreview = previewType === 'video';

  return (
    <div className="admin-page admin-page--media">
      <header className="admin-page-header">
        <h1>Media Uploads</h1>
        <p>Upload images and videos for the public media gallery. Draft items stay hidden until published.</p>
      </header>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <form className="admin-card admin-form admin-upload-form" onSubmit={handleSubmit}>
        <h2>{editingId ? 'Edit Media' : 'Upload Media'}</h2>

        <label className="admin-label">
          Title
          <input className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </label>

        <label className="admin-label">
          Caption / Description
          <textarea className="admin-textarea" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description shown on the gallery" />
        </label>

        <div className="admin-form-row">
          <label className="admin-label">
            Category
            <select className="admin-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>

          <label className="admin-label">
            Status
            <select className="admin-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
        </div>

        {!editingId && (
          <label className="admin-label">
            File (images up to 5MB, videos up to 50MB)
            <input ref={fileRef} className="admin-file-input" type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={handleFileChange} />
          </label>
        )}

        {previewUrl && (
          <div className="admin-media-preview admin-media-preview--form">
            {isVideoPreview ? (
              <video src={previewUrl} controls playsInline className="admin-media-preview-media" />
            ) : (
              <img src={previewUrl} alt="Preview" className="admin-media-preview-media" />
            )}
            <p className="admin-list-meta">Preview before publishing</p>
          </div>
        )}

        <div className="admin-form-actions">
          <button type="submit" className="admin-btn admin-btn-primary" disabled={uploading}>
            {uploading ? 'Saving...' : editingId ? 'Update' : 'Upload'}
          </button>
          {editingId && (
            <button type="button" className="admin-btn admin-btn-ghost" onClick={resetForm}>Cancel</button>
          )}
        </div>
      </form>

      <div className="admin-media-grid">
        {items.map((item) => (
          <article key={item.id} className="admin-media-card">
            <div className="admin-media-preview">
              {item.file_type === 'video' ? (
                <video src={item.file_url} muted playsInline preload="metadata" />
              ) : (
                <img src={item.file_url} alt={item.title} />
              )}
              <span className={`admin-badge admin-badge-${item.status}`}>{item.status}</span>
            </div>
            <div className="admin-media-info">
              <p className="admin-media-name">{item.title}</p>
              <p className="admin-list-meta">{item.category} · {item.file_type} · {new Date(item.created_at).toLocaleDateString()}</p>
              {item.description && <p className="admin-list-card-body">{item.description}</p>}
              <div className="admin-list-actions">
                <button type="button" className="admin-btn admin-btn-sm" onClick={() => handleEdit(item)}>Edit</button>
                <button type="button" className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleDelete(item.id)}>Delete</button>
              </div>
            </div>
          </article>
        ))}
        {!items.length && <p className="admin-empty">No uploads yet.</p>}
      </div>
    </div>
  );
}
